import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ClockAlert, RefreshCw, Loader2, Pencil, Lock } from 'lucide-react';
import { formatDate, formatDateTime12h } from '../../utils/timeFormat';

/**
 * /admin/clockout-hygiene
 *
 * Surfaces every booking whose actual clock duration exceeds
 * shift_hours × 1.5 — the hard gate that auto-payouts also enforce
 * via the CLOCKOUT_HYGIENE skip reason.
 *
 * Each row shows talent, venue, shift_date, expected hours, actual
 * clock duration, and a deep-link to the admin shift-detail page
 * where the admin can correct the clock-in/out times before approving
 * the booking for payout.
 */
const ClockoutHygiene = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/live-ops/clockout-hygiene');
      setRows(res.data?.rows || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = (d) => d ? formatDateTime12h(d) : '—';
  const dur = (h) => h == null ? '—' : `${Number(h).toFixed(2)}h`;

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="admin-clockout-hygiene-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <ClockAlert className="w-6 h-6 text-rose-600" />
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">Clock-Out Hygiene</h1>
          </div>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Bookings whose actual clock duration exceeds <span className="font-mono">shift_hours × 1.5</span>.
            These are automatically blocked from auto-payout via the <span className="font-mono">CLOCKOUT_HYGIENE</span> skip
            reason until an admin reviews the times and approves the corrected hours.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                data-testid="clockout-hygiene-refresh-btn" className="shrink-0">
          {loading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      <Card className="overflow-hidden border-rose-200">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning bookings…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-gray-500 text-sm" data-testid="clockout-hygiene-empty">
            ✓ No clock-out anomalies detected. All clocked-out bookings are within
            <span className="font-mono"> 1.5×</span> their shift duration.
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'auto' }}>
            <thead className="bg-rose-50 border-b border-rose-200 text-rose-900">
              <tr>
                <th className="px-3 py-2 text-left min-w-[160px]">Talent</th>
                <th className="px-3 py-2 text-left min-w-[220px]">Event</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Shift Date</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Expected</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Clock Duration</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Δ vs Expected</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Status</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.booking_id}
                    className={`border-b border-gray-100 ${i % 2 ? 'bg-gray-50/50' : ''}`}
                    data-testid={`clockout-hygiene-row-${r.booking_id}`}>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-gray-900" style={{ wordBreak: 'keep-all', overflowWrap: 'break-word' }}>
                      {r.talent_name}
                    </div>
                    <div className="mt-1">
                      {r.assignment_type === 'backup' ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] uppercase tracking-wide">
                          Backup Talent
                        </Badge>
                      ) : (
                        <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[10px] uppercase tracking-wide">
                          Primary Talent
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-800 align-top" style={{ wordBreak: 'keep-all', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                    {r.venue_name || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap align-top" data-testid={`clockout-hygiene-date-${r.booking_id}`}>
                    {formatDate(r.shift_date)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-700 font-mono align-top">{dur(r.expected_hours)}</td>
                  <td className="px-3 py-2 text-right font-mono text-rose-700 font-semibold align-top">
                    {dur(r.actual_clock_duration_hours)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-rose-600">
                    +{dur(r.delta_hours)}
                  </td>
                  <td className="px-3 py-2">
                    {r.auto_payout_blocked ? (
                      <Badge className="bg-rose-100 text-rose-800 border-rose-300 inline-flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Payout Blocked
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                        Needs Review
                      </Badge>
                    )}
                    {r.hours_approved && (
                      <div className="text-[10px] text-gray-500 mt-0.5">hours approved</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/admin/shifts/${r.shift_id}`}
                      data-testid={`clockout-hygiene-edit-${r.booking_id}`}
                    >
                      <Button size="sm" variant="outline" className="inline-flex items-center gap-1">
                        <Pencil className="w-3 h-3" /> Edit Times
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-rose-200 bg-rose-50/50">
                <td colSpan={8} className="px-3 py-2 text-[11px] text-rose-900">
                  {rows.length} booking{rows.length === 1 ? '' : 's'} flagged ·
                  Auto-payout cannot fire on these until clock times are corrected and re-approved.
                </td>
              </tr>
            </tfoot>
          </table>
          </div>
        )}
      </Card>

      <div className="mt-6 text-xs text-gray-500 max-w-3xl">
        <p>
          <strong>Why this list exists:</strong> a booking with a clock duration of (say) 24 hours
          on a 7-hour shift will compute payout as <span className="font-mono">rate × 24h</span> instead of
          <span className="font-mono"> rate × 7h</span>, leading to ~3× over-payment. The platform blocks
          auto-payout on these rows and surfaces them here so an admin can:
        </p>
        <ol className="list-decimal pl-5 mt-2 space-y-1">
          <li>Open the shift-detail page (Edit Times button).</li>
          <li>Correct the clock-in / clock-out timestamps.</li>
          <li>Re-approve hours, which recomputes <span className="font-mono">hours_worked</span> and <span className="font-mono">total_pay</span>.</li>
          <li>Unblock auto-payout (or fire Run-One LIVE) once the math is right.</li>
        </ol>
      </div>
    </div>
  );
};

export default ClockoutHygiene;
